import './style.scss';

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import moment from 'moment';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {
  DOWNLOAD_FILE,
  getById,
  reviewExtendImplementationTime,
} from 'apis/extendImplemetationTime.api';

import { getProjectById } from 'apis/projects.api';
import { formatFileName, getMonth, getYear, mapError } from 'utils/func';
import {
  useCreateAdjustmentComment,
  useGetAllAdjustmentComments,
} from 'utils/hooks/adjustmentComments';
import { toast } from 'layout';
import { ADJUSTMENT_TYPES, ADJUSTMENT_STATUSES, TOAST } from 'constant';
import {
  adjustmentCommentsSchema,
  adjustmentRejectSchema,
} from 'features/SubmitProjectProposal/validation';
import { DownloadFile, GeneralInfo, HeaderInfo } from '../Shared';
import AdjustmentComment from '../../AdjustmentComment';
import RejectModal from '../../RejectModal';

import ApproveModal from '../../ApproveModal';

export default function ExtendImplementationDetail({ id, isSumUp }) {
  const { t } = useTranslation();
  const { projectId, projectDeadlineExtensionId } = useParams();
  const navigate = useNavigate();

  const [appendixeArray, setAppendixeArray] = useState([]);
  const [fileUrl, setFileUrl] = useState(null);
  const [isCommentVisible, setIsCommentVisible] = useState(false);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [schema, setSchema] = useState(adjustmentCommentsSchema);
  const projectDeadlineExtensionIdUsed = projectDeadlineExtensionId || id;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, dirtyFields },
  } = useForm({ resolver: yupResolver(schema) });

  const { data: project } = useQuery(['getProjectById', projectId], () =>
    getProjectById(projectId)
  );

  const { data: _data, refetch } = useQuery(
    ['getExtendImplementationTimeById', projectDeadlineExtensionIdUsed],
    () => getById(projectDeadlineExtensionIdUsed)
  );
  const extendImplementationTimeData = useMemo(() => _data?.data ?? [], [_data]);

  const isRejectedOrApproved = useMemo(
    () => extendImplementationTimeData?.status === ADJUSTMENT_STATUSES.SUBMITTED ?? null,
    [extendImplementationTimeData]
  );

  const { mutate: mutateDownloadFile } = useMutation(DOWNLOAD_FILE);
  const { mutateAsync } = useCreateAdjustmentComment();
  const { mutateAsync: mutateAsyncReview } = useMutation(reviewExtendImplementationTime);
  const { data: adjustmentComments, refetch: refetchAdjustmentComments } =
    useGetAllAdjustmentComments(
      {
        adjustmentName: ADJUSTMENT_TYPES.PROJECT_DEADLINE_EXTENSION,
        adjustmentId: projectDeadlineExtensionIdUsed,
      },
      {
        enabled: !!projectDeadlineExtensionIdUsed,
      }
    );

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      { path },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  useEffect(() => {
    if (extendImplementationTimeData) {
      setFileUrl(extendImplementationTimeData?.fileUrl);
      if (extendImplementationTimeData?.extendImplementationTimeAppendixes?.length > 0) {
        setAppendixeArray(extendImplementationTimeData?.extendImplementationTimeAppendixes);
      }
    }
  }, [extendImplementationTimeData]);

  const onSubmitComment = async (data) => {
    const dataSender = {
      ...data,
      adjustmentName: ADJUSTMENT_TYPES.PROJECT_DEADLINE_EXTENSION,
      adjustmentId: projectDeadlineExtensionIdUsed,
    };
    try {
      const res = await mutateAsync(dataSender);
      if (res?.status.toString().startsWith('2')) {
        toast(TOAST.SUCCESS, t('projectAdjustment.commentSuccess'));
        refetchAdjustmentComments();
        reset();
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const onSubmit = async (data) => {
    const dataSender = {};
    if (rejectModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.REJECTED;
      dataSender.rejectionReason = data?.rejectionReason;
    } else if (approveModalVisible) {
      dataSender.status = ADJUSTMENT_STATUSES.APPROVED;
    }
    try {
      const res = await mutateAsyncReview({ id: projectDeadlineExtensionIdUsed, body: dataSender });
      if (res?.status.toString().startsWith('2')) {
        toast(
          TOAST.SUCCESS,
          t(`projectAdjustment.${rejectModalVisible ? 'rejectModal' : 'approveModal'}.success`)
        );
        refetch();
        setSchema(adjustmentCommentsSchema);
        setRejectModalVisible(false);
        setApproveModalVisible(false);
      }
    } catch (error) {
      toast(TOAST.ERROR, mapError(t, error));
    }
  };

  const onCloseRejectModal = () => {
    reset({}, { keepValues: true });
    setSchema(adjustmentCommentsSchema);
    setRejectModalVisible(false);
  };

  return (
    <div className="p-6 detail-extend-implementation-time-form">
      <DownloadFile id={projectDeadlineExtensionIdUsed} projectId={projectId} />
      <div className="p-5">
        <HeaderInfo />

        {/* info project */}
        <div className="mt-4">
          <GeneralInfo isDetail />

          <h5 className="font-semibold my-2">
            {t('extendImplementationTime.R07.implementationTimeSuggestExtend.title')}
            <span className="font-normal">
              {Math.ceil(
                moment(extendImplementationTimeData?.extendDate).diff(
                  project?.data?.endDate,
                  'months',
                  true
                )
              )}{' '}
              {t('extendImplementationTime.R07.implementationTimeFollowContract.sub')}{' '}
              {`${getMonth(project?.data?.endDate)}/${getYear(project?.data?.endDate)}`}{' '}
              {t('extendImplementationTime.R07.implementationTimeFollowContract.to')}{' '}
              {`${getMonth(extendImplementationTimeData?.extendDate)}/${getYear(
                extendImplementationTimeData?.extendDate
              )}`}
            </span>
          </h5>
          <h5 className="font-semibold my-2">
            {t('extendImplementationTime.R07.reason')}
            <span className="font-normal">{extendImplementationTimeData?.reason}</span>
          </h5>
          <h5 className="font-semibold my-2">
            {t('extendImplementationTime.R07.commit.title')}:{' '}
            <span className="font-normal">{t('extendImplementationTime.R07.commit.content')}</span>
          </h5>
        </div>

        {fileUrl && (
          <h5 className="font-semibold my-2">
            {t('extendImplementationTime.file')}:{' '}
            <span
              className="underline"
              style={{ color: 'blue', cursor: 'pointer' }}
              onClick={() => handleDownloadFile(fileUrl)}
            >
              {formatFileName(typeof fileUrl === 'string' ? fileUrl : fileUrl?.name)}
            </span>
          </h5>
        )}

        {appendixeArray?.length > 0 && (
          <h5 className="font-semibold my-2">{t('extendImplementationTime.attachedFiles')}:</h5>
        )}
        {appendixeArray?.map((appendixe) => (
          <div
            key={appendixe.id}
            className="mt-2 flex justify-content-between align-content-center"
          >
            <span
              key={appendixe?.fileUrl}
              className="py-2"
              style={{ minWidth: '16rem', cursor: 'pointer' }}
              onClick={() => handleDownloadFile(appendixe?.fileUrl)}
            >
              <i className="pi pi-file-pdf text-xl mr-2" style={{ color: 'red' }} />
              <span className="text-blue-500 underline font-italic text-base">
                {formatFileName(appendixe?.fileUrl)}
              </span>
            </span>
            <span className="py-4 flex-auto" />
          </div>
        ))}

        <AdjustmentComment
          visible={isCommentVisible}
          onClose={() => setIsCommentVisible(false)}
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          onSubmit={onSubmitComment}
          data={adjustmentComments}
          status={extendImplementationTimeData?.status}
          disabled={!(isDirty && Object.keys(dirtyFields).length) || rejectModalVisible}
        />

        <div
          className={`flex justify-content-end gap-4 ${
            !isSumUp || isRejectedOrApproved ? 'mt-6' : ''
          }`}
        >
          {!isSumUp && (
            <Button
              className="w-10rem"
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              label={t('extendImplementationTime.comeBack')}
              severity="danger"
              type="button"
              onClick={() => {
                navigate(-1);
              }}
            />
          )}
          {isRejectedOrApproved && (
            <>
              <Button
                className="w-10rem"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                label={t('projectAdjustment.reject')}
                severity="warning"
                type="button"
                onClick={() => {
                  setSchema(adjustmentRejectSchema);
                  setRejectModalVisible(true);
                }}
                disabled={!isRejectedOrApproved}
              />
              <Button
                className="w-16rem"
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                label={t('projectAdjustment.approve')}
                severity="success"
                type="button"
                onClick={() => setApproveModalVisible(true)}
                disabled={!isRejectedOrApproved}
              />
            </>
          )}
        </div>
      </div>

      <RejectModal
        visible={rejectModalVisible}
        onClose={onCloseRejectModal}
        control={control}
        errors={errors}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        isDeadline={false}
      />

      <ApproveModal
        visible={approveModalVisible}
        onClose={() => setApproveModalVisible(false)}
        onSubmit={onSubmit}
        isSubmitting={false}
      />
    </div>
  );
}

ExtendImplementationDetail.propTypes = {
  id: PropTypes.string,
  isSumUp: PropTypes.bool,
};

ExtendImplementationDetail.defaultProps = {
  id: null,
  isSumUp: false,
};
